<script setup lang="ts">
// Arbre des taxons (web + mobile). `selected` est la sélection COMPACTÉE (un parent remplace ses
// enfants quand ils sont tous cochés) ; en interne on l'étend aux feuilles pour AppTaxonNode, et
// on recompacte à chaque toggle avant d'émettre — ce qui minimise les valeurs de filtre.
import type { SpecieTaxonTreeResource } from "@carnet/api/backoffice";

const props = defineProps<{
  nodes: SpecieTaxonTreeResource[];
  selected: string[];
}>();

const emit = defineEmits<{
  "update:selected": [ids: string[]];
}>();

const expandedSet = computed(() => {
  const compact = new Set(props.selected);
  const result = new Set<string>();
  function walk(node: SpecieTaxonTreeResource, ancestorSelected: boolean) {
    const isSelected = ancestorSelected || compact.has(node.id);
    if (isSelected) result.add(node.id);
    for (const child of node.children) walk(child, isSelected);
  }
  for (const root of props.nodes) walk(root, false);
  return result;
});

const defaultOpenIds = computed(() => {
  const compact = new Set(props.selected);
  const openIds = new Set<string>();
  function walk(node: SpecieTaxonTreeResource, ancestorIds: string[]): boolean {
    let subtreeSelected = compact.has(node.id);
    for (const child of node.children) {
      if (walk(child, [...ancestorIds, node.id])) subtreeSelected = true;
    }
    if (subtreeSelected) for (const ancestorId of ancestorIds) openIds.add(ancestorId);
    return subtreeSelected;
  }
  for (const root of props.nodes) walk(root, []);
  return openIds;
});

function compactSelection(roots: SpecieTaxonTreeResource[], expanded: Set<string>): string[] {
  const result: string[] = [];
  function walk(node: SpecieTaxonTreeResource): boolean {
    if (node.children.length === 0) {
      if (expanded.has(node.id)) {
        result.push(node.id);
        return true;
      }
      return false;
    }
    const before = result.length;
    let allFull = true;
    for (const child of node.children) {
      if (!walk(child)) allFull = false;
    }
    if (allFull) {
      result.length = before;
      result.push(node.id);
      return true;
    }
    return false;
  }
  for (const root of roots) walk(root);
  return result;
}

function onToggle(ids: string[], checked: boolean) {
  const next = new Set(expandedSet.value);
  if (checked) for (const id of ids) next.add(id);
  else for (const id of ids) next.delete(id);
  emit("update:selected", compactSelection(props.nodes, next));
}
</script>

<template>
  <ul class="flex flex-col">
    <AppTaxonNode v-for="node in nodes" :key="node.id" :node="node" :selected="expandedSet" :default-open="defaultOpenIds" :depth="0" @toggle="onToggle" />
  </ul>
</template>
